// src/api/queries/templates.js
/**
 * Хуки шаблонов: list/listByKind, create/update/delete, apply/:id/apply,
 * POST /apply.
 *
 * useInstantiateTemplateMutation — материализация payloads в проекте
 * (POST /templates/apply { projectId, parentTaskId, payloads }):
 *   - ModalRenderer.handleTaskSave (parentTaskId = <id задачи>): создаёт
 *     подзадачи. Родитель мог стать is_summary (TaskService._recalcSummaryChain),
 *     поэтому инвалидируется и ["task", parentTaskId].
 *   - ProjectModal.saveHandler (parentTaskId = null): создаёт задачи
 *     проекта; сам проект не менялся — только ["tasks"].
 * onSuccess инвалидирует ["tasks"] (и ["task", parentTaskId] при наличии),
 * чтобы созданные подзадачи/задачи сразу появлялись в кэше: staleTime 30s
 * иначе отложил бы refetch до следующего монтирования/фокуса.
 *
 * useApplyTemplateMutation — старый путь POST /templates/:id/apply; сейчас
 * на фронте не используется (заменён POST /templates/apply), оставлен для
 * совместимости/будущего.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";

export const TEMPLATES_KEY = ["templates"];

export function useTemplatesQuery(options = {}) {
  return useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: () => api.get("/templates"),
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useTemplatesByKindQuery(kind, options = {}) {
  return useQuery({
    queryKey: [...TEMPLATES_KEY, "kind", kind],
    queryFn: () => api.get(`/templates?kind=${kind}`),
    enabled: !!kind,
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useCreateTemplateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post("/templates", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

export function useUpdateTemplateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => api.patch(`/templates/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

export function useDeleteTemplateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/templates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TEMPLATES_KEY });
    },
  });
}

export function useApplyTemplateMutation() {
  return useMutation({
    mutationFn: ({ id, projectId }) =>
      api.post(`/templates/${id}/apply`, { projectId }),
  });
}

export function useInstantiateTemplateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ payloads, projectId, parentTaskId }) =>
      api.post(`/templates/apply`, { payloads, projectId, parentTaskId }),
    onSuccess: (_res, variables) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      if (variables?.parentTaskId) {
        qc.invalidateQueries({ queryKey: ["task", variables.parentTaskId] });
      }
    },
  });
}
